import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API_ENDPOINTS from '../Config/api';
import { useSetRecoilState } from 'recoil';
import { disableForEnterpriseAtom } from '../Atom/disableForEnterpriseAtom';

/**
 * Extract SSO token from URL fragment, query string, or cookie (fallback).
 * Returns the token string or null. Cleans up the source after reading.
 */
function extractTokenFromURLOrCookie() {
  // 1. URL fragment (#token=…) — not sent to server
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  let token = hashParams.get('token');

  // 2. Query string (?token=…) — backward compat
  if (!token) {
    const urlParams = new URLSearchParams(window.location.search);
    token = urlParams.get('token');
  }

  // Scrub token from URL/history immediately
  if (token) {
    window.history.replaceState({}, document.title, window.location.pathname);
    return token;
  }

  // 3. Cookie fallback
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const trimmed = cookie.trim();
    const eqIdx = trimmed.indexOf('=');
    if (eqIdx === -1) continue;
    const name = trimmed.substring(0, eqIdx);
    const value = trimmed.substring(eqIdx + 1);
    if (name === 'sso_token') {
      document.cookie = 'sso_token=; path=/; max-age=0';
      document.cookie = 'sso_token=; path=/; domain=' + window.location.hostname + '; max-age=0';
      return decodeURIComponent(value);
    }
  }

  return null;
}

export default function SsoInterceptor() {

  const navigate = useNavigate();
  const setDisableForEnterprise = useSetRecoilState(disableForEnterpriseAtom);
  const calledRef = useRef(false);

  const validateAndRedirect = useCallback(async (token) => {
    if (calledRef.current) return;

    if (!token) {
      console.warn('SSO: nessun token trovato');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('refresh_token');
      navigate('/');
      return;
    }

    calledRef.current = true;
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');

    try {
      const res = await fetch(API_ENDPOINTS.validateSsoToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      if (!res.ok) {
        console.error('Token non valido o errore HTTP:', res.status);
        navigate('/');
        return;
      }

      const data = await res.json();

      if (data?.access) {
        sessionStorage.setItem('access_token', data.access);
        if (data.refresh) {
          sessionStorage.setItem('refresh_token', data.refresh);
        }
        if (data.impersonation_source) {
          sessionStorage.setItem('sso_impersonation', data.impersonation_source.toLowerCase());
          if (data.impersonation_source.toLowerCase() === 'enterprise') {
            setDisableForEnterprise(true);
          }
        }
        navigate('/dashboard');
      } else {
        navigate('/');
      }
    } catch (err) {
      console.error('Errore nella validazione token SSO:', err);
      navigate('/');
    }
  }, [navigate, setDisableForEnterprise]);

  useEffect(() => {
    // Primary method: receive token via postMessage from the MSSP opener window.
    // The token never appears in the URL — no logs, no Referer, no visual flash.
    if (window.opener) {
      let settled = false;

      const onMessage = (event) => {
        if (settled) return;
        if (event.source !== window.opener) return;
        if (event.data?.type !== 'sso-token' || !event.data?.token) return;
        settled = true;
        window.removeEventListener('message', onMessage);
        validateAndRedirect(event.data.token);
      };

      window.addEventListener('message', onMessage);

      // Tell the opener we're ready to receive the token
      try { window.opener.postMessage('sso-ready', '*'); } catch (_) { /* opener gone */ }

      // Fallback: if opener doesn't respond in 3s, try URL/cookie methods
      const fallbackTimer = setTimeout(() => {
        if (settled) return;
        settled = true;
        window.removeEventListener('message', onMessage);
        validateAndRedirect(extractTokenFromURLOrCookie());
      }, 3000);

      return () => {
        window.removeEventListener('message', onMessage);
        clearTimeout(fallbackTimer);
      };
    }

    // No opener (direct navigation, bookmark, etc.) — fallback to URL/cookie
    validateAndRedirect(extractTokenFromURLOrCookie());
  }, [validateAndRedirect]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-white">
      <div className="flex items-center gap-3">
        <svg
          className="w-6 h-6 text-yellow-600 animate-spin"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
          ></path>
        </svg>
        <p className="text-gray-800 font-medium animate-pulse text-2xl">Accesso in corso...</p>
      </div>
    </div>
  );
}
